import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {useRejectionRates} from '../hooks/useRejectionRates';

import {DoughnutRates} from './DoughnutRates';
import {Loading} from '../../../../../components/Loading/Loading';
import {greenColors} from '../../../../../layouts/theme/designSystem';

interface Props {
  companyId: string;
}

export const RejectionsRates = ({companyId}: Props) => {
  const {t} = useTranslation();
  const td = (key: string) => t(`companyRecruitingDashboard.rejections.${key}`);

  const renderColour = greenColors[100];
  const renderBackgroundColour = greenColors[40];
  const {data, loading, error} = useRejectionRates(companyId);

  const serializeData = (percentage: number) => {
    const rejectionRate = percentage;

    const remaining = 100 - rejectionRate;

    return {
      datasets: [
        {
          data: [remaining, rejectionRate],
          backgroundColor: [renderBackgroundColour, renderColour],
          borderWidth: 0,
        },
      ],
    };
  };

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          minHeight: '20vh',
        }}
      >
        <Loading />
      </Box>
    );

  if (error && !loading)
    return (
      <Box my={5}>
        <Typography align='center' variant='h4'>
          {td('errorHandling')}
        </Typography>
      </Box>
    );

  return (
    <Grid item container mt={8}>
      {/* Company rejections */}
      <Grid
        item
        container
        xs={12}
        sm={6}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: {xs: 4, sm: 0},
        }}
      >
        <Typography variant='h2' gutterBottom>
          {td('employerRejection')}
        </Typography>
        {data?.companyRejections.percentage ? (
          <Box ml={2}>
            <DoughnutRates
              data={serializeData(data.companyRejections.percentage)}
              rejectionPercentage={data.companyRejections.percentage}
              deparmentData={data.companyRejections.prevalentDepartment}
            />
          </Box>
        ) : (
          <Typography variant='h4'>{td('emptyState')}</Typography>
        )}
      </Grid>
      {/* Company rejections */}

      {/* Talents rejections */}
      <Grid
        item
        container
        xs={12}
        sm={6}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant='h2' gutterBottom>
          {td('talentRejection')}
        </Typography>
        {data?.applicantRejections.percentage ? (
          <Box ml={2}>
            <DoughnutRates
              data={serializeData(data.applicantRejections.percentage)}
              rejectionPercentage={data.applicantRejections.percentage}
              deparmentData={data.applicantRejections.prevalentDepartment}
            />
          </Box>
        ) : (
          <Typography variant='h4'>{td('emptyState')}</Typography>
        )}
      </Grid>
      {/* Talents rejections */}
    </Grid>
  );
};
